/**
 * src/components/Financial/DailyEventsBlock.jsx
 * 【エラー解消版】複合アスペクト計算を内蔵し、週間カレンダーと完全に同期
 */
import React, { useMemo } from "react";
import * as Astro from "../../astronomy.js";
import { Clock, Star, Activity, Sun, Zap } from "lucide-react";
import { PLANET_SYMBOLS } from "../../utils/constants.js";
import { styles as commonStyles } from "../../utils/styles.js";

// --- 週間カレンダーと共通の天体リスト ---
const MAJOR_BODIES = [
  "Sun",
  "Moon",
  "Mercury",
  "Venus",
  "Mars",
  "Jupiter",
  "Saturn",
  "Uranus",
  "Neptune",
  "Pluto",
];

// --- 除外すべき軸ペア（常に180度になるもの） ---
const AXIS_PAIRS = [
  ["ASC", "DSC"],
  ["MC", "IC"],
  ["NorthNode", "SouthNode"],
];

/**
 * 複合アスペクト（パターン）を計算する内部ヘルパー
 * astronomy.js に関数がない場合のエラーを回避するためここに定義
 */
const calculatePatternsInternal = (positions, orbLimit = 8.0) => {
  const patterns = [];
  const bodies = Object.keys(positions).filter((p) => MAJOR_BODIES.includes(p));

  // 天体間の角度をマトリックス化
  const aspects = [];
  for (let i = 0; i < bodies.length; i++) {
    for (let j = i + 1; j < bodies.length; j++) {
      const p1 = bodies[i];
      const p2 = bodies[j];
      const diff = Math.abs(positions[p1] - positions[p2]) % 360;
      const angle = diff > 180 ? 360 - diff : diff;
      aspects.push({ p1, p2, angle });
    }
  }

  const findAsp = (p1, p2, target, orb) => {
    const asp = aspects.find(
      (a) => (a.p1 === p1 && a.p2 === p2) || (a.p1 === p2 && a.p2 === p1),
    );
    if (!asp) return false;
    return Math.abs(asp.angle - target) <= orb;
  };

  // T-Square 判定
  for (let i = 0; i < bodies.length; i++) {
    for (let j = 0; j < bodies.length; j++) {
      for (let k = j + 1; k < bodies.length; k++) {
        const apex = bodies[i];
        const opp1 = bodies[j];
        const opp2 = bodies[k];
        if (apex === opp1 || apex === opp2) continue;

        if (
          findAsp(opp1, opp2, 180, orbLimit) &&
          findAsp(apex, opp1, 90, orbLimit) &&
          findAsp(apex, opp2, 90, orbLimit)
        ) {
          patterns.push({ name: "T-Square", planets: [apex, opp1, opp2] });
        }
      }
    }
  }

  // Grand Trine 判定
  for (let i = 0; i < bodies.length; i++) {
    for (let j = i + 1; j < bodies.length; j++) {
      for (let k = j + 1; k < bodies.length; k++) {
        const p1 = bodies[i],
          p2 = bodies[j],
          p3 = bodies[k];
        if (
          findAsp(p1, p2, 120, orbLimit) &&
          findAsp(p2, p3, 120, orbLimit) &&
          findAsp(p3, p1, 120, orbLimit)
        ) {
          patterns.push({ name: "Grand Trine", planets: [p1, p2, p3] });
        }
      }
    }
  }

  // 重複削除
  return patterns.filter(
    (v, i, a) =>
      a.findIndex(
        (t) =>
          t.name === v.name &&
          t.planets.sort().join() === v.planets.sort().join(),
      ) === i,
  );
};

export default function DailyEventsBlock(props) {
  const {
    transitDate,
    isHeliocentric,
    isSidereal,
    planetColors,
    aspectSettings,
    isDarkMode,
    isMobile,
    transitTz,
  } = props;

  const events = useMemo(
    function () {
      if (!transitDate) return [];

      const tzOffsetMs = transitTz * 3600000;
      const year = transitDate.getFullYear();
      const month = transitDate.getMonth();
      const day = transitDate.getDate();

      const dayStartUTC = Date.UTC(year, month, day, 0, 0, 0) - tzOffsetMs;
      const dayEndUTC = dayStartUTC + 24 * 3600000;

      var results = [];

      try {
       // --- 1. 当日中のExactアスペクト (成立の瞬間を精密検索) ---
// --- 1. 当日中のExactアスペクト (成立の瞬間を精密検索) ---
        // aspectType: "major" を明示的に指定し、マイナーアスペクトを除外します
        var standardResults = Astro.searchAspectsExtended({
          fromDate: new Date(dayStartUTC),
          toDate: new Date(dayEndUTC),
          isHeliocentric: isHeliocentric,
          isSidereal: isSidereal,
          aspectType: "major", 
          aspectSettings: aspectSettings,
        });

        var exactKeys = [];
        if (standardResults) {
          standardResults.forEach(function (hit) {
            var t = hit.date.getTime();
            if (t >= dayStartUTC && t < dayEndUTC) {
              if (MAJOR_BODIES.indexOf(hit.planet1) !== -1 && MAJOR_BODIES.indexOf(hit.planet2) !== -1) {
                results.push({ type: "standard", time: t, data: hit });
                exactKeys.push([hit.planet1, hit.planet2, hit.aspect.name].sort().join("|"));
              }
            }
          });
        }

        // --- 2. 当日 Orb 0.1°以下のタイトなアスペクトを抽出 (ALL DAY相当) ---
        // ここでも Astro.ASPECTS.major のみに限定し、マイナーアスペクトは含めません
        var midDayDate = new Date(dayStartUTC + 12 * 3600000);
        var pos = {};
        MAJOR_BODIES.forEach(function(p) {
          pos[p] = isHeliocentric 
            ? Astro.getHeliocentricLongitude(p, midDayDate)
            : Astro.getPlanetLongitude(p, midDayDate);
        });

        var checkAspects = Astro.ASPECTS.major; 
        for (var i = 0; i < MAJOR_BODIES.length; i++) {
          for (var j = i + 1; j < MAJOR_BODIES.length; j++) {
            var p1 = MAJOR_BODIES[i];
            var p2 = MAJOR_BODIES[j];
            if (pos[p1] === null || pos[p2] === null) continue;

            var asp = Astro.calculateAspect(pos[p1], pos[p2], checkAspects, p1, p2, aspectSettings);

            if (asp && asp.orb <= 0.1) {
              var key = [p1, p2, asp.name].sort().join("|");
              if (exactKeys.indexOf(key) === -1) {
                results.push({
                  type: "standard",
                  time: dayStartUTC,
                  data: {
                    planet1: p1,
                    planet2: p2,
                    aspect: { name: asp.name, symbol: asp.symbol },
                    orb: asp.orb
                  }
                });
              }
            }
          }
        }

        // --- 3. 新月・満月・食 (名称の重複を解消) ---
        // (中略)
        var eclipseList = Astro.getEclipses ? Astro.getEclipses(transitDate, 365) : [];
        eclipseList.forEach(function (ecl) {
          var eTime = new Date(ecl.date).getTime();
          if (eTime >= dayStartUTC && eTime < dayEndUTC) {
            results.push({
              type: "lunation",
              time: eTime,
              name: ecl.type, // japaneseKind との連結を削除し重複を解消
              symbol: ecl.symbol || "⚠️",
              label: "Eclipse",
            });
          }
        });

        // 3. ステーション (逆行・順行開始)
     MAJOR_BODIES.forEach(function (p) {
        if (p === "Sun" || p === "Moon" || p === "ASC" || p === "MC") return;
        var s1 = Astro.getPlanetSpeed(p, new Date(dayStartUTC), isHeliocentric);
        var s2 = Astro.getPlanetSpeed(p, new Date(dayEndUTC), isHeliocentric);
        if (s1 !== null && s2 !== null) {
          if (s1.speedPerDay * s2.speedPerDay < 0) {
            results.push({
              type: "station",
              time: dayStartUTC + 12 * 3600000,
              name: s2.speedPerDay < 0 ? p + " 逆行開始 (R)" : p + " 順行開始 (D)",
              symbol: s2.speedPerDay < 0 ? "℞" : "🔄",
              label: "Station",
            });
          }
        }
      });

// --- 4. サイン入り (Ingress) の追加 ---
      MAJOR_BODIES.forEach(function (p) {
        if (p === "ASC" || p === "MC") return;
        var d1 = new Date(dayStartUTC);
        var d2 = new Date(dayEndUTC);
        var lon1 = isHeliocentric ? Astro.getHeliocentricLongitude(p, d1) : Astro.getPlanetLongitude(p, d1);
        var lon2 = isHeliocentric ? Astro.getHeliocentricLongitude(p, d2) : Astro.getPlanetLongitude(p, d2);
        
        var sign1 = Astro.getZodiacSign(lon1, isSidereal, d1);
        var sign2 = Astro.getZodiacSign(lon2, isSidereal, d2);

        if (sign1 && sign2 && sign1.name !== sign2.name) {
          results.push({
            type: "ingress",
            time: dayStartUTC + 12 * 3600000,
            planet: p,
            signSymbol: sign2.symbol,
            signName: sign2.name,
            symbol: "✨",
            label: "Ingress"
          });
        }
      });


        // 4. 二十四節気 (名称特定ロジック)
        var solar1 = Astro.getPlanetLongitude(
          "Sun",
          new Date(dayStartUTC),
          false,
        );
        var solar2 = Astro.getPlanetLongitude(
          "Sun",
          new Date(dayEndUTC),
          false,
        );
        if (solar1 !== null && solar2 !== null) {
          var termIdx1 = Math.floor(solar1 / 15);
          var termIdx2 = Math.floor(solar2 / 15);
          if (termIdx1 !== termIdx2) {
            const SOLAR_TERMS_JP = [
              "春分",
              "清明",
              "穀雨",
              "立夏",
              "小満",
              "芒種",
              "夏至",
              "小暑",
              "大暑",
              "立秋",
              "処暑",
              "白露",
              "秋分",
              "寒露",
              "霜降",
              "立冬",
              "小雪",
              "大雪",
              "冬至",
              "小寒",
              "大寒",
              "立春",
              "雨水",
              "啓蟄",
            ];
            var termName = SOLAR_TERMS_JP[termIdx2 % 24];
            results.push({
              type: "solar_term",
              time: dayStartUTC + 12 * 3600000, // 正午付近として表示
              name: termName, // 「二十四節気の切り替わり」から名称へ変更
              symbol: "☀️",
              label: "SolarTerm",
            });
          }
        }

        return results.sort(function (a, b) {
          return a.time - b.time;
        });
      } catch (e) {
        console.error("Calculation Error:", e);
        return [];
      }
    },
    [transitDate, isHeliocentric, isSidereal, aspectSettings, transitTz],
  );

  const formatTzTime = (ts) => {
    const d = new Date(ts + transitTz * 3600000);
    return (
      d.getUTCHours().toString().padStart(2, "0") +
      ":" +
      d.getUTCMinutes().toString().padStart(2, "0")
    );
  };

  return (
    <div style={{ gridColumn: "span 12", width: "100%", marginBottom: "20px" }}>
      <div style={commonStyles.dynamicBlockCard(isDarkMode, isMobile)}>
        <div style={commonStyles.dynamicBlockTitle(isDarkMode)}>
          <Zap size={18} color="#f1c40f" />
          <span>
            本日の重要イベント ({isSidereal ? "Sidereal" : "Tropical"})
          </span>
        </div>

        <div style={{ padding: isMobile ? "10px" : "0 24px 24px 24px" }}>
          {events.length > 0 ? (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
{events.map(function (ev, idx) {
                if (ev.type === "standard") {
                  var hit = ev.data;
                  var isHard = ["Conjunction", "Square", "Opposition"].indexOf(hit.aspect.name) !== -1;
                  return (
                    <div key={idx} style={itemSt(isDarkMode)}>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1 }}>
                        <span style={{ fontSize: "20px", color: planetColors[hit.planet1] || "#8b5cf6" }}>
                          {PLANET_SYMBOLS[hit.planet1]}
                        </span>
                        <span style={{ fontSize: "16px", color: isHard ? "#e74c3c" : "#3498db" }}>
                          {hit.aspect.symbol}
                        </span>
                        <span style={{ fontSize: "20px", color: planetColors[hit.planet2] || "#8b5cf6" }}>
                          {PLANET_SYMBOLS[hit.planet2]}
                        </span>
                        
                        {/* イベント名：太字・指定色・星座記号（色付き・紫背景なし） */}
                        <span
                          style={{
                            fontSize: "13px",
                            fontWeight: "bold",
                            color: isDarkMode ? "#fff" : "rgb(136, 136, 136)",
                          }}
                        >
                          {hit.planet1}
                          <span style={{ color: planetColors[hit.planet1] || "#8b5cf6", marginLeft: "2px" }}>
                            ({hit.sign1 ? hit.sign1.symbol + "\uFE0E" : ""})
                          </span>
                          {" "}{hit.aspect.name}{" "}
                          {hit.planet2}
                          <span style={{ color: planetColors[hit.planet2] || "#8b5cf6", marginLeft: "2px" }}>
                            ({hit.sign2 ? hit.sign2.symbol + "\uFE0E" : ""})
                          </span>
                        </span>
                      </div>
                      <div style={badgeSt(isHard)}>
                        {isHard ? "HARD" : "SOFT"}
                      </div>
                    </div>
                  );
                }

                // 節気・ステーション・イングレス・月相の共通表示
                var accentColor = ev.type === "solar_term" ? "#e67e22" : (ev.type === "station" ? "#9b59b6" : "#64748b");
                return (
                  <div
                    key={idx}
                    style={Object.assign({}, itemSt(isDarkMode), {
                      borderLeft: "4px solid " + accentColor,
                      borderRight: "none",
                      borderTop: "none",
                      borderBottom: "none",
                    })}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1 }}>
                      <span style={{ fontSize: "20px" }}>{ev.symbol}</span>
                      <span
                        style={{
                          fontSize: "14px",
                          fontWeight: "bold",
                          color: isDarkMode ? "#fff" : "rgb(136, 136, 136)",
                        }}
                      >
                        {ev.type === "ingress" ? (
                          <>
                            {ev.planet}
                            <span style={{ color: planetColors[ev.planet] || "#8b5cf6", marginLeft: "4px" }}>
                              ({ev.signSymbol + "\uFE0E"})
                            </span>
                            {" "}{ev.signName}入り
                          </>
                        ) : (
                          ev.name
                        )}
                      </span>
                    </div>
                    <div style={badgeSt(true, accentColor)}>{ev.label}</div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div
              style={{
                padding: "30px",
                textAlign: "center",
                color: isDarkMode ? "#6b7280" : "#94a3b8",
                fontSize: "14px",
              }}
            >
              トランジット日の、指定タイムゾーンでの重要イベントはありません。
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// スタイル補助
const itemSt = (dark) => ({
  display: "flex",
  alignItems: "center",
  gap: "15px",
  padding: "12px 16px",
  background: dark ? "rgba(255, 255, 255, 0.03)" : "#f8fafc",
  borderRadius: "10px",
  // Warning回避のため border プロパティを個別に定義
  borderWidth: "1px",
  borderStyle: "solid",
  borderColor: dark ? "rgba(255,255,255,0.05)" : "#f1f5f9",
});
const timeSt = (dark) => ({
  display: "flex",
  alignItems: "center",
  gap: "5px",
  minWidth: "85px",
  color: dark ? "#9ca3af" : "#64748b",
  fontSize: "13px",
  fontWeight: "bold",
});
const labelSt = (dark) => ({
  fontSize: "13px",
  fontWeight: "bold",
  color: dark ? "#e5e7eb" : "#475569",
  marginLeft: "5px",
});
const badgeSt = (hard, color) => ({
  fontSize: "10px",
  padding: "2px 8px",
  borderRadius: "4px",
  background: color
    ? "rgba(241, 196, 15, 0.1)"
    : hard
      ? "rgba(231, 76, 60, 0.1)"
      : "rgba(52, 152, 219, 0.1)",
  color: color || (hard ? "#e74c3c" : "#3498db"),
  fontWeight: "bold",
});
